import { smartSearch } from "../lib/utils";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Truck, Package, Calendar, MapPin, Clock, CheckCircle2,
  ChevronRight, Loader2, RefreshCw, Phone, Search,
  Home, Store, AlertCircle, CalendarDays, XCircle, User
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_LABELS = {
  morning: "Matin (9h-12h)",
  afternoon: "Apres-midi (14h-17h)",
  evening: "Fin de journee (17h-19h)"
};

const STATUS_COLORS = {
  ready:      "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  delivered:  "bg-blue-500/20 text-blue-400 border-blue-500/30",
  in_repair:  "bg-amber-500/20 text-amber-400 border-amber-500/30",
};

const STATUS_LABELS = {
  ready: "Pret",
  delivered: "Livre",
  in_repair: "En reparation",
  testing: "Test",
  reception: "Reception"
};

function RepairCard({ repair, onClick }) {
  const isHome = repair.service_type === "domicile";
  const daysSinceReady = repair.updated_at
    ? Math.floor((Date.now() - new Date(repair.updated_at)) / 86400000)
    : 0;

  return (
    <button
      onClick={() => onClick(repair)}
      className={`w-full text-left p-4 rounded-xl border transition-all hover:scale-[1.01] hover:shadow-lg ${
        isHome
          ? "bg-violet-500/5 border-violet-500/30 hover:border-violet-400/60"
          : "bg-card border-border hover:border-primary/40"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            isHome ? "bg-violet-500/20" : "bg-emerald-500/20"
          }`}>
            {isHome ? <Home className="w-4 h-4 text-violet-400" /> : <Store className="w-4 h-4 text-emerald-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-primary font-semibold">{repair.ticket_number}</span>
              {isHome && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30 font-medium">
                  A domicile
                </span>
              )}
              {daysSinceReady > 2 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  {daysSinceReady}j en attente
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-foreground mt-0.5 truncate">{repair.client_name}</p>
            <p className="text-xs text-muted-foreground truncate">{repair.device_brand} {repair.device_model}</p>
            {isHome && repair.address_street && (
              <p className="text-xs text-violet-400 mt-1 flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3 shrink-0" />
                {repair.address_street}, {repair.address_postal_code} {repair.address_city}
              </p>
            )}
            {repair.pickup_appointment && (
              <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3 shrink-0" />
                RDV : {repair.pickup_appointment.date} — {TIME_LABELS[repair.pickup_appointment.time_slot] || repair.pickup_appointment.time_slot}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {repair.client_phone && (
            <a
              href={`tel:${repair.client_phone}`}
              onClick={(e) => e.stopPropagation()}
              className="p-1.5 rounded-lg bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground transition-colors"
              title="Appeler le client"
            >
              <Phone className="w-3.5 h-3.5" />
            </a>
          )}
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    </button>
  );
}

function AppointmentCard({ apt, onClick }) {
  const isToday = apt.preferred_date === new Date().toISOString().split("T")[0];
  const isTomorrow = apt.preferred_date === new Date(Date.now() + 86400000).toISOString().split("T")[0];
  const dayLabel = isToday ? "Aujourd'hui" : isTomorrow ? "Demain" : apt.preferred_date;

  return (
    <button
      onClick={() => onClick(apt)}
      className={`w-full text-left p-4 rounded-xl border transition-all hover:scale-[1.01] ${
        isToday
          ? "bg-emerald-500/10 border-emerald-500/40 hover:border-emerald-400"
          : "bg-card border-border hover:border-primary/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center shrink-0 ${
          isToday ? "bg-emerald-500/20" : "bg-primary/10"
        }`}>
          <Calendar className={`w-4 h-4 ${isToday ? "text-emerald-400" : "text-primary"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${isToday ? "text-emerald-400" : "text-foreground"}`}>
              {dayLabel}
            </span>
            <span className="text-xs text-muted-foreground">{TIME_LABELS[apt.preferred_time_slot] || apt.preferred_time_slot}</span>
          </div>
          <p className="text-sm font-medium text-foreground truncate">{apt.client_name}</p>
          <p className="text-xs text-muted-foreground">
            {apt.device_brand} {apt.device_model}
            {apt.ticket_number && <span className="ml-2 font-mono text-primary">{apt.ticket_number}</span>}
          </p>
        </div>
        {isToday && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-bold shrink-0">
            Aujourd'hui
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </div>
    </button>
  );
}

export default function DeliveryPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("ready"); // ready | appointments | domicile | history
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [proposeDateOpen, setProposeDateOpen] = useState(false);
  const [proposedDate, setProposedDate] = useState("");
  const [proposedTime, setProposedTime] = useState("morning");
  const [proposedMsg, setProposedMsg] = useState("");
  const [proposeSubmitting, setProposeSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/delivery/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRepairClick = (repair) => {
    setSelectedRepair(repair);
    setDetailOpen(true);
  };

  const handleAptClick = (apt) => {
    if (apt.repair_id) navigate(`/repairs/${apt.repair_id}`);
  };

  const handleProposeDate = async () => {
    if (!proposedDate || !selectedRepair) return;
    setProposeSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const aptId = selectedRepair.pickup_appointment?.appointment_id;
      if (aptId) {
        await axios.post(
          `${API_URL}/api/admin/pickup-appointments/${aptId}/propose-date`,
          {},
          {
            params: { proposed_date: proposedDate, proposed_time_slot: proposedTime, message: proposedMsg },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }
      toast.success("Nouvelle date envoyee au client par email");
      setProposeDateOpen(false);
      setDetailOpen(false);
      setProposedDate("");
      setProposedMsg("");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la proposition");
    } finally {
      setProposeSubmitting(false);
    }
  };

  const filterItems = (items) => {
    if (!search) return items;
    return items.filter(r =>
      smartSearch(search, [r.client_name, r.ticket_number, r.device_brand, r.device_model, r.client_phone])
    );
  };

  const tabs = [
    { id: "ready", label: "Prets", icon: Package, count: data?.stats?.total_ready },
    { id: "appointments", label: "RDV", icon: Calendar, count: data?.stats?.total_appointments },
    { id: "domicile", label: "Domicile", icon: Home, count: data?.stats?.total_domicile },
    { id: "history", label: "Recents", icon: CheckCircle2, count: data?.stats?.delivered_this_week },
  ];

  const currentItems = () => {
    if (!data) return [];
    switch (activeTab) {
      case "ready":        return filterItems(data.ready_repairs || []);
      case "appointments": return data.upcoming_appointments || [];
      case "domicile":     return filterItems(data.domicile_repairs || []);
      case "history":      return filterItems(data.recent_delivered || []);
      default:             return [];
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-chivo text-2xl font-bold text-foreground flex items-center gap-2">
              <Truck className="w-6 h-6 text-primary" />
              Livraisons
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Gestion des restitutions et rendez-vous
            </p>
          </div>
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </Button>
        </div>

        {/* Stats cards */}
        {data && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "En attente", value: data.stats.total_ready, color: "text-emerald-400", bg: "bg-emerald-500/10", icon: Package },
              { label: "RDV prevus", value: data.stats.total_appointments, color: "text-primary", bg: "bg-primary/10", icon: Calendar },
              { label: "A domicile", value: data.stats.total_domicile, color: "text-violet-400", bg: "bg-violet-500/10", icon: Home },
              { label: "Livres (7j)", value: data.stats.delivered_this_week, color: "text-blue-400", bg: "bg-blue-500/10", icon: CheckCircle2 },
            ].map(({ label, value, color, bg, icon: Icon }) => (
              <Card key={label} className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${color}`} />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${color}`}>{value}</p>
                      <p className="text-xs text-muted-foreground">{label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                    activeTab === tab.id ? "bg-white/20 text-white" : "bg-primary/20 text-primary"
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Search (sauf pour appointments) */}
        {activeTab !== "appointments" && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher client, ticket, appareil..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-secondary/50"
            />
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-3">
            {currentItems().length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Aucun element a afficher</p>
              </div>
            ) : activeTab === "appointments" ? (
              currentItems().map((apt) => (
                <AppointmentCard key={apt.id} apt={apt} onClick={handleAptClick} />
              ))
            ) : (
              currentItems().map((repair) => (
                <RepairCard key={repair.id} repair={repair} onClick={handleRepairClick} />
              ))
            )}
          </div>
        )}
      </div>

      {/* Dialog detail réparation */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-chivo flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              {selectedRepair?.ticket_number}
            </DialogTitle>
          </DialogHeader>
          {selectedRepair && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-secondary/50">
                  <p className="text-xs text-muted-foreground mb-1">Client</p>
                  <p className="text-sm font-medium">{selectedRepair.client_name}</p>
                  {selectedRepair.client_phone && (
                    <a href={`tel:${selectedRepair.client_phone}`} className="text-xs text-primary flex items-center gap-1 mt-1">
                      <Phone className="w-3 h-3" />{selectedRepair.client_phone}
                    </a>
                  )}
                </div>
                <div className="p-3 rounded-lg bg-secondary/50">
                  <p className="text-xs text-muted-foreground mb-1">Appareil</p>
                  <p className="text-sm font-medium">{selectedRepair.device_brand}</p>
                  <p className="text-xs text-muted-foreground">{selectedRepair.device_model}</p>
                </div>
              </div>

              {selectedRepair.service_type === "domicile" && (
                <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/30">
                  <p className="text-xs text-violet-400 font-medium mb-1 flex items-center gap-1">
                    <Home className="w-3 h-3" /> Livraison a domicile
                  </p>
                  <p className="text-sm text-foreground">{selectedRepair.address_street}</p>
                  {selectedRepair.address_complement && <p className="text-xs text-muted-foreground">{selectedRepair.address_complement}</p>}
                  <p className="text-xs text-muted-foreground">{selectedRepair.address_postal_code} {selectedRepair.address_city}</p>
                </div>
              )}

              {selectedRepair.pickup_appointment && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <p className="text-xs text-emerald-400 font-medium mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> RDV de recuperation
                  </p>
                  <p className="text-sm text-foreground font-bold">{selectedRepair.pickup_appointment.date}</p>
                  <p className="text-xs text-muted-foreground">{TIME_LABELS[selectedRepair.pickup_appointment.time_slot]}</p>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button
                  className="flex-1 bg-primary hover:bg-primary/90"
                  onClick={() => { setDetailOpen(false); navigate(`/repairs/${selectedRepair.id}`); }}
                >
                  Voir la fiche
                </Button>
                {selectedRepair.pickup_appointment && (
                  <Button
                    variant="outline"
                    className="border-amber-500 text-amber-500 hover:bg-amber-500/10"
                    onClick={() => { setDetailOpen(false); setProposeDateOpen(true); }}
                  >
                    <CalendarDays className="w-4 h-4 mr-1" />
                    Autre date
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog proposer une autre date */}
      <Dialog open={proposeDateOpen} onOpenChange={setProposeDateOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-chivo flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-amber-500" />
              Proposer une autre date
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedRepair && (
              <div className="p-3 rounded-lg bg-secondary/50 text-sm">
                <p className="font-medium">{selectedRepair.client_name}</p>
                <p className="text-muted-foreground">RDV actuel : {selectedRepair.pickup_appointment?.date}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Nouvelle date *</Label>
              <Input
                type="date"
                value={proposedDate}
                onChange={(e) => setProposedDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="bg-secondary/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Creneau</Label>
              <select
                value={proposedTime}
                onChange={(e) => setProposedTime(e.target.value)}
                className="w-full p-2 rounded-md border border-border bg-secondary/50 text-sm"
              >
                <option value="morning">Matin (9h-12h)</option>
                <option value="afternoon">Apres-midi (14h-17h)</option>
                <option value="evening">Fin de journee (17h-19h)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Message (optionnel)</Label>
              <Textarea
                value={proposedMsg}
                onChange={(e) => setProposedMsg(e.target.value)}
                placeholder="Ex : Le creneau demande n'est pas disponible..."
                className="bg-secondary/50 text-sm"
                rows={2}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setProposeDateOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button
                onClick={handleProposeDate}
                disabled={proposeSubmitting || !proposedDate}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
              >
                {proposeSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
